/**
 * useTable Hook
 *
 * Custom hook to manage table state including row expansion, sorting, and infinite scroll.
 * Follows AiGuideLine.md: "Make a custom Hook to handle the State"
 *
 * Features:
 * - Row expansion state management
 * - Toggle individual rows
 * - Expand/collapse all rows
 * - Check if row is expanded
 * - Sorting functionality
 * - Infinite scroll detection
 *
 * @example
 * const { expandedRows, toggleRowExpansion, isRowExpanded, sortConfig, handleSort } = useTable();
 */

import { useState, useCallback, useMemo } from 'react';

const useTable = () => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  /**
   * Toggle expansion state of a specific row
   * @param {number} rowIndex - Index of the row to toggle
   */
  const toggleRowExpansion = useCallback((rowIndex) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  }, []);

  /**
   * Check if a row is expanded
   * @param {number} rowIndex - Index of the row to check
   * @returns {boolean} True if row is expanded
   */
  const isRowExpanded = useCallback(
    (rowIndex) => expandedRows.has(rowIndex),
    [expandedRows]
  );

  /**
   * Expand all rows
   * @param {number} totalRows - Total number of rows
   */
  const expandAllRows = useCallback((totalRows) => {
    const allRows = new Set();
    for (let i = 0; i < totalRows; i++) {
      allRows.add(i);
    }
    setExpandedRows(allRows);
  }, []);

  /**
   * Collapse all rows
   */
  const collapseAllRows = useCallback(() => {
    setExpandedRows(new Set());
  }, []);

  /**
   * Reset expansion state
   */
  const resetExpansion = useCallback(() => {
    setExpandedRows(new Set());
  }, []);

  /**
   * Handle sorting
   * @param {string} key - Column key to sort by
   */
  const handleSort = useCallback((key) => {
    setSortConfig((prevConfig) => {
      // If clicking the same column, toggle direction
      if (prevConfig.key === key) {
        if (prevConfig.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return { key: null, direction: null }; // Reset sorting
        }
      }
      // New column, start with ascending
      return { key, direction: 'asc' };
    });
  }, []);

  /**
   * Sort data based on current sort configuration
   * @param {Array} data - Data to sort
   * @returns {Array} Sorted data
   */
  const sortData = useCallback(
    (data) => {
      if (!sortConfig.key || !sortConfig.direction) {
        return data;
      }

      return [...data].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        // Compare values
        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else {
          comparison = String(aValue).localeCompare(String(bValue));
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    },
    [sortConfig]
  );

  /**
   * Create scroll handler for infinite scroll
   * @param {function} onLoadMore - Callback to load more data
   * @param {boolean} hasMore - Whether there's more data to load
   * @param {boolean} isLoading - Whether data is currently loading
   * @param {number} threshold - Distance from bottom to trigger load (in pixels)
   * @returns {function} Scroll event handler
   */
  const createScrollHandler = useCallback(
    (onLoadMore, hasMore, isLoading, threshold = 100) => {
      return (event) => {
        const target = event.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // Calculate distance from bottom
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        // Trigger load more if close to bottom and conditions are met
        if (
          distanceFromBottom < threshold &&
          hasMore &&
          !isLoading &&
          onLoadMore
        ) {
          onLoadMore();
        }
      };
    },
    []
  );

  return {
    expandedRows,
    toggleRowExpansion,
    isRowExpanded,
    expandAllRows,
    collapseAllRows,
    resetExpansion,
    sortConfig,
    handleSort,
    sortData,
    createScrollHandler,
  };
};

export default useTable;
